import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { resetPassword } from "../api/authApi";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { useToast } from "../context/ToastContext";
import {
  CheckCircle,
  AlertTriangle,
  Loader2,
  Eye,
  EyeOff,
  Lock,
  Check,
  Globe,
  Sun,
  Moon,
  Monitor,
} from "lucide-react";
import "./Auth.css";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language, toggleLanguage } = useLanguage();
  const { themePreference, setThemePreference } = useTheme();
  const { addToast } = useToast();

  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);

  useEffect(() => {
    const urlEmail = searchParams.get("email");
    const urlToken = searchParams.get("token");

    if (!urlEmail || !urlToken) {
      setInvalidLink(true);
    } else {
      setEmail(urlEmail);
      setToken(urlToken);
    }
  }, [searchParams]);

  const passwordChecks = useMemo(() => {
    return {
      length: password.length >= 8 && password.length <= 30,
    };
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!passwordChecks.length) {
      setError(t("resetPassword.errPasswordLength"));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("resetPassword.errPasswordMatch"));
      return;
    }

    setLoading(true);

    try {
      await resetPassword(email, password, token);
      setSuccess(true);
      if (addToast) {
        addToast(t("resetPassword.toastSuccess"), "success");
      }
    } catch (err) {
      setError(err.message || t("resetPassword.errFailedDefault"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page-container">
        {/* Quick Language & Theme Controls */}
        <div className="auth-toolbar">
          {/* Language Switcher */}
          <button
            type="button"
            className="auth-util-btn"
            onClick={toggleLanguage}
            title={language === "ar" ? "Switch to English" : "التبديل إلى العربية"}
            aria-label={language === "ar" ? "Switch to English" : "التبديل إلى العربية"}
          >
            <Globe size={14} />
            <span>{language === "ar" ? "English" : "العربية"}</span>
          </button>

          {/* Theme Segmented Switcher */}
          <div className="auth-theme-group" role="group" aria-label={t("verifyEmail.themeMode")}>
            <button
              type="button"
              className={`auth-theme-option ${themePreference === "light" ? "is-active" : ""}`}
              onClick={() => setThemePreference("light")}
              title={t("verifyEmail.themeLight")}
              aria-label={t("verifyEmail.themeLight")}
              aria-pressed={themePreference === "light"}
            >
              <Sun size={13} />
              <span className="auth-theme-text">{t("verifyEmail.themeLight")}</span>
            </button>
            <button
              type="button"
              className={`auth-theme-option ${themePreference === "dark" ? "is-active" : ""}`}
              onClick={() => setThemePreference("dark")}
              title={t("verifyEmail.themeDark")}
              aria-label={t("verifyEmail.themeDark")}
              aria-pressed={themePreference === "dark"}
            >
              <Moon size={13} />
              <span className="auth-theme-text">{t("verifyEmail.themeDark")}</span>
            </button>
            <button
              type="button"
              className={`auth-theme-option ${themePreference === "system" ? "is-active" : ""}`}
              onClick={() => setThemePreference("system")}
              title={t("verifyEmail.themeSystem")}
              aria-label={t("verifyEmail.themeSystem")}
              aria-pressed={themePreference === "system"}
            >
              <Monitor size={13} />
              <span className="auth-theme-text">{t("verifyEmail.themeSystem")}</span>
            </button>
          </div>
        </div>

        {/* Reset Password Card */}
        <div
          className="auth-card"
          style={{
            textAlign: invalidLink || success ? "center" : undefined,
            padding: "40px 32px",
          }}
        >
          {invalidLink ? (
            <div>
              <div className="auth-status-icon-wrap auth-status-icon--error">
                <AlertTriangle size={48} />
              </div>
              <h2 className="auth-title">{t("resetPassword.invalidTitle")}</h2>
              <p className="auth-subtitle">{t("resetPassword.invalidSubtitle")}</p>
              <Link
                to="/forgot-password"
                className="btn-primary"
                style={{ display: "inline-block", marginTop: "24px" }}
              >
                {t("resetPassword.requestNewLink")}
              </Link>
            </div>
          ) : success ? (
            <div>
              <div className="auth-status-icon-wrap auth-status-icon--gold">
                <CheckCircle size={48} />
              </div>
              <h2 className="auth-title">{t("resetPassword.successTitle")}</h2>
              <p className="auth-subtitle">{t("resetPassword.successSubtitle")}</p>
              <Link
                to="/login"
                className="btn-primary"
                style={{ display: "inline-block", marginTop: "24px" }}
              >
                {t("resetPassword.proceedToSignIn")}
              </Link>
            </div>
          ) : (
            <>
              <div className="auth-header" style={{ textAlign: "center", marginBottom: "24px" }}>
                <h2 className="auth-title">{t("resetPassword.title")}</h2>
                <p className="auth-subtitle">{t("resetPassword.subtitle")}</p>
              </div>

              <form className="auth-form" onSubmit={handleSubmit} style={{ textAlign: "start" }}>
                {error && (
                  <div className="auth-error" role="alert">
                    {error}
                  </div>
                )}

                <div className="auth-field">
                  <label htmlFor="password">{t("resetPassword.passwordLabel")}</label>
                  <div className="auth-input-wrapper auth-input-wrapper--ltr">
                    <span className="auth-input-icon">
                      <Lock size={16} />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      className="has-toggle"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("resetPassword.passwordPlaceholder")}
                      autoComplete="new-password"
                      dir="ltr"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="auth-toggle-pwd"
                      aria-label={
                        showPassword
                          ? t("resetPassword.hidePassword")
                          : t("resetPassword.showPassword")
                      }
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="auth-field">
                  <label htmlFor="confirmPassword">
                    {t("resetPassword.confirmPasswordLabel")}
                  </label>
                  <div className="auth-input-wrapper auth-input-wrapper--ltr">
                    <span className="auth-input-icon">
                      <Lock size={16} />
                    </span>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      className="has-toggle"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t("resetPassword.confirmPasswordPlaceholder")}
                      autoComplete="new-password"
                      dir="ltr"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="auth-toggle-pwd"
                      aria-label={
                        showConfirmPassword
                          ? t("resetPassword.hidePassword")
                          : t("resetPassword.showPassword")
                      }
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Password Criteria Checklist */}
                <div className="password-requirements">
                  <div className="password-requirements-title">
                    {t("resetPassword.requirementsTitle")}
                  </div>
                  <div className="password-req-list">
                    <span className={`req-item ${passwordChecks.length ? "met" : ""}`}>
                      <Check size={13} /> {t("resetPassword.reqLength")}
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary auth-submit"
                  disabled={loading}
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  {loading ? (
                    <>
                      <Loader2
                        size={16}
                        className="auth-spinner"
                      />
                      <span style={{ marginInlineStart: "8px" }}>
                        {t("resetPassword.submitting")}
                      </span>
                    </>
                  ) : (
                    t("resetPassword.submitBtn")
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
